import { FormattedMessage, useIntl } from "react-intl";
import { Text, Card, Title, Stack, Group, Button } from "@mantine/core";
import { InjectSkeletonLoaders } from "../../../components/InjectSkeletonLoaders.js";
import type { UsageCardLoaderProps } from "./UsageCard.js";
import type { TieredPriceProductId } from "@project/common";
import { wideCard } from "./UsageAndBilling.module.css";
import { useEstimatedBill } from "./hooks/fetchSums.js";
import { useMemo } from "react";
import { addMonths, endOfMonth, format, startOfMonth } from "date-fns";
import { ErrorAlert } from "./ErrorAlert.js";
import { dateFnsLocales } from "../../../constants/dateFnsLocales.js";
import clsx from "clsx";

interface BillCardProps extends Omit<UsageCardLoaderProps, "productId"> {
  productIds: TieredPriceProductId[];
}

interface EstimatedTotalBillCardProps extends Omit<BillCardProps, "from" | "to"> {
  showButton?: boolean;
}

const BillingCardError = () => (
  <ErrorAlert>
    <FormattedMessage
      id="billing.error"
      defaultMessage="Unable to load billing data. Please try again later."
    />
  </ErrorAlert>
);

/**
 * Displays the estimated total bill for the current billing period, broken
 * down by category.
 */
export const EstimatedTotalBillCard = ({
  interval,
  apiKeyId,
  productIds,
  showButton = true,
  isLoadingSubscriptions,
}: EstimatedTotalBillCardProps) => {
  const intl = useIntl();

  const { bill, isLoading, isError } = useEstimatedBill({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
    interval,
    apiKeyId,
    productIds,
    isLoadingSubscriptions,
  });

  const startOfNextMonth = useMemo(() => format(
    startOfMonth(addMonths(new Date(), 1)),
    "PP",
    { locale: dateFnsLocales[intl.locale] },
  ), [intl.locale]);

  return (
    <Card variant="shadowless" className={clsx(wideCard, "grow")} p="md">
      <Stack gap="sm">
        <Title order={5} fw={600}>
          <FormattedMessage
            id="billing.title"
            defaultMessage="Estimated for this billing period"
          />
        </Title>

        {isError ?
            <BillingCardError />
          :
            (
              <Group justify="space-between">
                <InjectSkeletonLoaders isLoading={isLoading}>
                  <Stack gap="sm">
                    <Text fz="h1" fw={600}>{bill?.grandTotal}</Text>
                    <Text c="dimmed" fz="sm">
                      <FormattedMessage
                        id="billing.nextDate"
                        defaultMessage="Next billing date {startOfNextMonth}"
                        values={{ startOfNextMonth }}
                      />
                    </Text>
                  </Stack>

                  <Stack gap={4} w={200}>
                    {bill?.receipt.map(({ category, label, subtotal }) => (
                      <Group justify="space-between" key={category}>
                        <Text fz="sm">{label}</Text>
                        <Text fz="sm" fw={600}>{subtotal}</Text>
                      </Group>
                    ))}
                  </Stack>
                </InjectSkeletonLoaders>
              </Group>
            )}

        {showButton && (
          <Button variant="primary" w="max-content">
            <FormattedMessage
              id="billing.button"
              defaultMessage="View detailed billing"
            />
          </Button>
        )}
      </Stack>
    </Card>
  );
};

/** Estimated total bill for any billing period, broken down by category. */
interface EstimatedPeriodBillCardProps extends BillCardProps {
  periodTitle: string;
}

export const EstimatedPeriodBillCard = ({
  from,
  to,
  interval,
  apiKeyId,
  productIds,
  periodTitle,
  isLoadingSubscriptions,
}: EstimatedPeriodBillCardProps) => {
  const intl = useIntl();

  const { bill, isLoading, isError } = useEstimatedBill({
    from,
    to,
    interval,
    apiKeyId,
    productIds,
    isLoadingSubscriptions,
  });

  return (
    <Card variant="shadowless" className={clsx(wideCard, "grow")} p="md">
      <Stack gap="sm">
        <Title order={5} fw={600}>
          {periodTitle}
        </Title>

        {isError ?
            <BillingCardError />
          :
            (
              <Group justify="space-between">
                <InjectSkeletonLoaders isLoading={isLoading}>
                  <Stack gap="sm">
                    <Text fz="h1" fw={600}>{bill?.grandTotal}</Text>
                    <Text c="dimmed" fz="sm">
                      <FormattedMessage
                        id="billing.period"
                        defaultMessage="{from} - {to}"
                        values={{
                          from: format(
                            from,
                            "P",
                            { locale: dateFnsLocales[intl.locale] },
                          ),
                          to: format(
                            to,
                            "P",
                            { locale: dateFnsLocales[intl.locale] },
                          ),
                        }}
                      />
                    </Text>
                  </Stack>

                  <Stack gap={4} w={200}>
                    {bill?.receipt.map(({ category, label, subtotal }) => (
                      <Group justify="space-between" key={category}>
                        <Text fz="sm">{label}</Text>
                        <Text fz="sm" fw={600}>{subtotal}</Text>
                      </Group>
                    ))}
                  </Stack>
                </InjectSkeletonLoaders>
              </Group>
            )}
      </Stack>
    </Card>
  );
};
